import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { ShieldUser, VenetianMask, X } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface SupportBarProps {
  /** The signed-in user; while masquerading, the support user behind the masquerade (may be unknown). */
  username?: string
  /** The user being masqueraded as, if any. */
  masqueradeAs?: string
  onStopMasquerade?: () => void
  /** Support links shown on the right, normally `SupportBarLink`s. */
  children?: ReactNode
}

const strong = <strong className="font-semibold" />
const pill = (
  <span className="rounded-full bg-(--ad-support) px-2 py-px font-semibold text-(--ad-support-foreground)" />
)

/**
 * Slim bar above the top bar for Adnuntius staff: shows which super admin is signed in,
 * or who is masquerading as whom, with room for support links on the right.
 */
export function SupportBar({ username, masqueradeAs, onStopMasquerade, children }: SupportBarProps) {
  const { t } = useTranslation()
  const BarIcon = masqueradeAs ? VenetianMask : ShieldUser

  return (
    <div
      role="region"
      aria-label={t('supportBar.label')}
      className="flex min-h-8 flex-wrap items-center gap-x-6 gap-y-1 border-b border-(--ad-support-border) bg-(--ad-support-bg) px-6 py-1 text-xs text-(--ad-support) max-md:px-3"
    >
      <div className="flex min-w-0 items-center gap-2">
        <BarIcon className="size-3.5 shrink-0" />
        <span className="truncate">
          {masqueradeAs ? (
            username ? (
              <Trans
                i18nKey="supportBar.masqueradingAs"
                values={{ username, target: masqueradeAs }}
                components={{ strong, pill }}
              />
            ) : (
              <Trans
                i18nKey="supportBar.masquerading"
                values={{ target: masqueradeAs }}
                components={{ pill }}
              />
            )
          ) : (
            <Trans i18nKey="supportBar.signedInAs" values={{ username }} components={{ strong }} />
          )}
        </span>
        {masqueradeAs && onStopMasquerade && (
          <Button
            variant="ghost"
            size="xs"
            className="text-current hover:bg-(--ad-support-border) hover:text-current"
            onClick={onStopMasquerade}
          >
            <X />
            {t('masquerade.stop')}
          </Button>
        )}
      </div>
      {children && <nav className="ml-auto flex items-center gap-4">{children}</nav>}
    </div>
  )
}

/** A link inside the support bar. */
export function SupportBarLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="font-medium text-current underline-offset-4 opacity-80 hover:underline hover:opacity-100"
    >
      {children}
    </Link>
  )
}
